import React, { useState } from "react";
import { Smile } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

import { toast } from "sonner";

export const ALLOWED_REACTIONS = ["👍", "❤️", "🔥", "😂", "😮", "😢"];

/**
 * MessageReactions — render existing reaction chips + an emoji picker.
 *
 * Props:
 *  - reactions: array of {reactorId, emoji}
 *  - myReactorId: the current user's reactor id ("admin" for admin side, user.id for artist)
 *  - onToggle: async (emoji) => updatedMessage
 *  - align: "left" | "right"  (which side the picker pops from)
 *  - theme: "light" | "dark"
 */
export default function MessageReactions({
  reactions = [],
  myReactorId,
  onToggle,
  align = "left",
  theme = "dark",
}) {
  const [open, setOpen] = useState(false);

  // Group by emoji
  const groups = {};
  for (const r of reactions) {
    if (!groups[r.emoji]) groups[r.emoji] = [];
    groups[r.emoji].push(r);
  }
  const entries = Object.entries(groups);

  const pickerLight = theme === "light";

  const handle = async (emoji) => {
    setOpen(false);
    try {
      await onToggle(emoji);
    } catch (e) {
      console.error("reaction failed:", e);
      toast.error("Reaction failed");
    }
  };

  if (entries.length === 0) {
    // Only show the "add reaction" affordance — visible on tap on touch devices, hover-reveal on desktop.
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="reaction-add-btn"
            className={`md:opacity-0 md:group-hover:opacity-100 opacity-60 transition-opacity h-6 w-6 rounded-full flex items-center justify-center ${
              pickerLight ? "text-gray-400 hover:text-navy hover:bg-gray-100" : "text-white/40 hover:text-white hover:bg-white/10"
            }`}
            aria-label="Add reaction"
          >
            <Smile className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={align === "right" ? "end" : "start"}
          sideOffset={6}
          className={`p-1.5 w-auto ${pickerLight ? "bg-white border-gray-200" : "bg-[#161a2c] border-white/10"}`}
        >
          <div className="flex gap-0.5">
            {ALLOWED_REACTIONS.map((e) => (
              <button
                key={e}
                type="button"
                data-testid={`reaction-pick-${e}`}
                onClick={() => handle(e)}
                className={`text-lg w-8 h-8 rounded-md hover:scale-125 transition-transform ${
                  pickerLight ? "hover:bg-gray-100" : "hover:bg-white/10"
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className={`flex flex-wrap gap-1 mt-1 ${align === "right" ? "justify-end" : "justify-start"}`}>
      {entries.map(([emoji, list]) => {
        const reactedByMe = list.some((r) => r.reactorId === myReactorId);
        return (
          <button
            key={emoji}
            type="button"
            data-testid={`reaction-chip-${emoji}`}
            onClick={() => handle(emoji)}
            className={`text-xs flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
              reactedByMe
                ? pickerLight
                  ? "bg-paul-yellow/20 border-paul-yellow text-navy"
                  : "bg-paul-yellow/20 border-paul-yellow/60 text-white"
                : pickerLight
                  ? "bg-gray-100 border-gray-200 text-gray-700 hover:border-gray-300"
                  : "bg-white/5 border-white/10 text-white/80 hover:border-white/20"
            }`}
          >
            <span>{emoji}</span>
            <span className="font-semibold">{list.length}</span>
          </button>
        );
      })}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="reaction-add-btn"
            className={`text-xs h-6 w-6 rounded-full flex items-center justify-center ${
              pickerLight ? "text-gray-400 hover:text-navy hover:bg-gray-100" : "text-white/40 hover:text-white hover:bg-white/10"
            }`}
            aria-label="Add reaction"
          >
            <Smile className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={align === "right" ? "end" : "start"}
          sideOffset={6}
          className={`p-1.5 w-auto ${pickerLight ? "bg-white border-gray-200" : "bg-[#161a2c] border-white/10"}`}
        >
          <div className="flex gap-0.5">
            {ALLOWED_REACTIONS.map((e) => (
              <button
                key={e}
                type="button"
                data-testid={`reaction-pick-${e}`}
                onClick={() => handle(e)}
                className={`text-lg w-8 h-8 rounded-md hover:scale-125 transition-transform ${
                  pickerLight ? "hover:bg-gray-100" : "hover:bg-white/10"
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
